import type { ReactNode } from 'react';
import type { GameStakeConfig, ScoringType, StakesConfig } from '@/lib/games/engine/types';
import { appliesMoney, defaultStakeForFormat, getLegStakes, scoringTypeHint, supportsStakeBasis } from '@/lib/games/engine/stakes-helpers';
import { HANDICAP_ALLOWANCE } from '@/lib/games/handicap';
import { catalogLabel } from '@/lib/games/game-catalog';
import { HandicapAllowancePicker } from '@/components/shared/HandicapAllowancePicker';
import { ClubPointsInfoCard } from './ClubPointsInfoCard';
import { NassauMatchBuilder } from './NassauMatchBuilder';
import type { NassauMatchConfig } from '@/lib/games/engine/types';

const SCORING_TYPES: { id: ScoringType; label: string }[] = [
  { id: 'none', label: 'None' },
  { id: 'points', label: 'Points' },
  { id: 'money', label: 'Money' },
  { id: 'both', label: 'Both' },
];

const MATCH_FORMAT_IDS = new Set(['match-play']);

function NetHandicapAllowance({
  formatId,
  config,
  update,
  forceShow = false,
}: {
  formatId: string;
  config: GameStakeConfig;
  update: (id: string, patch: Partial<GameStakeConfig>) => void;
  forceShow?: boolean;
}) {
  if (!forceShow && config.scoringMode === 'gross') return null;
  const value = config.handicapAllowance ?? HANDICAP_ALLOWANCE[formatId] ?? HANDICAP_ALLOWANCE.match ?? 1;
  return (
    <HandicapAllowancePicker
      formatId={formatId}
      formatName={catalogLabel(formatId)}
      value={value}
      onChange={(a) => update(formatId, { handicapAllowance: a })}
    />
  );
}

interface RosterEntry {
  player_key: string;
  name: string;
  isOrganizer?: boolean;
}

interface Props {
  enabledIds: string[];
  stakes: StakesConfig;
  onChange: (stakes: StakesConfig) => void;
  roster?: RosterEntry[];
  organizerKey?: string;
  opponentKeys?: Record<string, string>;
  onOpponentChange?: (formatId: string, opponentKey: string) => void;
  nassauMatches?: Record<string, NassauMatchConfig[]>;
  onNassauMatchesChange?: (formatId: string, matches: NassauMatchConfig[]) => void;
  teams?: string[][];
}

export function StakesScoringPanel({
  enabledIds,
  stakes,
  onChange,
  roster,
  organizerKey,
  opponentKeys,
  onOpponentChange,
  nassauMatches,
  onNassauMatchesChange,
  teams,
}: Props) {
  if (!enabledIds.length) {
    return (
      <div className="rounded-xl border border-dashed border-line px-4 py-8 text-center text-sm text-mute">
        Turn on at least one game on the previous step.
      </div>
    );
  }

  const updateGame = (formatId: string, patch: Partial<GameStakeConfig>) => {
    const current = stakes[formatId] ?? defaultStakeForFormat(formatId);
    onChange({ ...stakes, [formatId]: { ...current, ...patch } });
  };

  const updateLegMoney = (
    formatId: string,
    leg: 'front' | 'back' | 'total',
    value: number,
  ) => {
    const current = stakes[formatId] ?? defaultStakeForFormat(formatId);
    const legs = getLegStakes(current);
    legs[leg] = value;
    onChange({ ...stakes, [formatId]: { ...current, money: legs } });
  };

  return (
    <div className="space-y-4">
      <ClubPointsInfoCard />

      {enabledIds.map((formatId) => {
        const config = stakes[formatId] ?? defaultStakeForFormat(formatId);
        const showMoney = appliesMoney(config);

        return (
          <div key={formatId} className="rounded-xl border border-line bg-paper p-3.5">
            <div className="mb-2 text-sm font-bold text-ink">{catalogLabel(formatId)}</div>

            <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-mute">Game scoring</div>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {SCORING_TYPES.map((t) => (
                <Chip
                  key={t.id}
                  active={config.scoringType === t.id}
                  onClick={() => updateGame(formatId, { scoringType: t.id })}
                  label={t.label}
                />
              ))}
            </div>
            <p className="mb-3 text-[10px] leading-relaxed text-mute">
              {scoringTypeHint(config.scoringType)}
            </p>

            {renderGameOptions(
              formatId,
              config,
              showMoney,
              updateGame,
              updateLegMoney,
              roster,
              organizerKey,
              opponentKeys,
              onOpponentChange,
              nassauMatches,
              onNassauMatchesChange,
              teams,
            )}
          </div>
        );
      })}
    </div>
  );
}

function renderGameOptions(
  formatId: string,
  config: GameStakeConfig,
  showMoney: boolean,
  update: (id: string, patch: Partial<GameStakeConfig>) => void,
  updateLeg: (id: string, leg: 'front' | 'back' | 'total', v: number) => void,
  roster?: RosterEntry[],
  organizerKey?: string,
  opponentKeys?: Record<string, string>,
  onOpponentChange?: (formatId: string, opponentKey: string) => void,
  nassauMatches?: Record<string, NassauMatchConfig[]>,
  onNassauMatchesChange?: (formatId: string, matches: NassauMatchConfig[]) => void,
  teams?: string[][],
) {
  const legs = getLegStakes(config);
  const matchOpponentPicker = MATCH_FORMAT_IDS.has(formatId) && roster && roster.length > 1 && organizerKey && onOpponentChange
    ? (
      <MatchOpponentPicker
        formatId={formatId}
        roster={roster}
        organizerKey={organizerKey}
        opponentKey={opponentKeys?.[formatId]}
        onChange={onOpponentChange}
      />
    )
    : null;

  if (formatId === 'stroke' || formatId === 'net-stroke') {
    return (
      <div className="space-y-2">
        <OptionRow label="Basis">
          <Chip active={formatId === 'stroke'} onClick={() => {}} label="Low Gross" disabled />
          <Chip active={formatId === 'net-stroke'} onClick={() => {}} label="Low Net" disabled />
        </OptionRow>
        <p className="text-[10px] text-mute">
          {formatId === 'stroke' ? 'Lowest gross wins the leaderboard.' : 'Lowest net wins the leaderboard.'}
        </p>
        {showMoney && (
          <>
            <StakeBasisRow config={config} formatId={formatId} update={update} totalLabel="Total" />
            <StakeRow
              label={config.matchStakeMode === 'per-hole' ? 'Per hole' : 'Overall stake'}
              suffix={config.matchStakeMode === 'per-hole' ? 'per hole won' : 'winner takes pot'}
              value={typeof config.money === 'number' ? config.money : 10}
              onChange={(v) => update(formatId, { money: v })}
            />
          </>
        )}
        {!showMoney && <p className="text-[10px] text-mute">Leaderboard only — no money on this game.</p>}
        {formatId === 'net-stroke' && (
          <NetHandicapAllowance formatId={formatId} config={config} update={update} forceShow />
        )}
      </div>
    );
  }

  if (formatId === 'match-play') {
    return (
      <div className="space-y-2">
        {matchOpponentPicker}
        <OptionRow label="Stake basis">
          <Chip
            active={config.matchStakeMode !== 'per-hole'}
            onClick={() => update(formatId, { matchStakeMode: 'per-match' })}
            label="Per match"
          />
          <Chip
            active={config.matchStakeMode === 'per-hole'}
            onClick={() => update(formatId, { matchStakeMode: 'per-hole' })}
            label="Per hole"
          />
        </OptionRow>
        <OptionRow label="Scoring">
          <Chip
            active={config.scoringMode !== 'gross'}
            onClick={() => update(formatId, { scoringMode: 'net' })}
            label="Net"
          />
          <Chip
            active={config.scoringMode === 'gross'}
            onClick={() => update(formatId, { scoringMode: 'gross' })}
            label="Gross"
          />
        </OptionRow>
        <NetHandicapAllowance formatId={formatId} config={config} update={update} />
        {showMoney && (
          <StakeRow
            label={config.matchStakeMode === 'per-hole' ? 'Per hole' : 'Per match'}
            suffix="bet amount"
            value={typeof config.money === 'number' ? config.money : 10}
            onChange={(v) => update(formatId, { money: v })}
          />
        )}
      </div>
    );
  }

  if (formatId === 'skins') {
    return (
      <div className="space-y-2">
        <OptionRow label="Scoring">
          <Chip
            active={config.scoringMode !== 'gross'}
            onClick={() => update(formatId, { scoringMode: 'net' })}
            label="Net"
          />
          <Chip
            active={config.scoringMode === 'gross'}
            onClick={() => update(formatId, { scoringMode: 'gross' })}
            label="Gross"
          />
        </OptionRow>
        <NetHandicapAllowance formatId={formatId} config={config} update={update} />
        <OptionRow label="Carryover">
          <Chip
            active={config.carryover !== false}
            onClick={() => update(formatId, { carryover: true })}
            label="On"
          />
          <Chip
            active={config.carryover === false}
            onClick={() => update(formatId, { carryover: false })}
            label="Off"
          />
        </OptionRow>
        {showMoney && (
          <StakeRow
            label="Per hole"
            suffix="skin value"
            value={typeof config.money === 'number' ? config.money : 5}
            onChange={(v) => update(formatId, { money: v })}
          />
        )}
      </div>
    );
  }

  if (formatId === 'nassau' || formatId === 'team-nassau') {
    const fmt = formatId as 'nassau' | 'team-nassau';
    return (
      <div className="space-y-2">
        {roster && onNassauMatchesChange && (
          <NassauMatchBuilder
            formatId={fmt}
            roster={roster}
            teams={teams}
            matches={nassauMatches?.[formatId] || []}
            defaultStakes={config}
            onChange={(m) => onNassauMatchesChange(formatId, m)}
          />
        )}
        <OptionRow label="Scoring">
          <Chip
            active={config.scoringMode !== 'gross'}
            onClick={() => update(formatId, { scoringMode: 'net' })}
            label="Net"
          />
          <Chip
            active={config.scoringMode === 'gross'}
            onClick={() => update(formatId, { scoringMode: 'gross' })}
            label="Gross"
          />
        </OptionRow>
        <NetHandicapAllowance formatId={formatId} config={config} update={update} />
        {showMoney && (
          <p className="text-[10px] leading-relaxed text-mute">
            Set per-match stakes on each matchup above, or use the default leg amounts when adding matches.
            {' '}Presses are started manually during the round.
          </p>
        )}
      </div>
    );
  }

  if (formatId === 'stableford') {
    return (
      <div className="space-y-2">
        <NetHandicapAllowance formatId={formatId} config={config} update={update} forceShow />
        {showMoney && (
          <>
            <StakeBasisRow config={config} formatId={formatId} update={update} totalLabel="Total" />
            {config.matchStakeMode === 'per-hole' ? (
              <StakeRow
                label="Per hole"
                suffix="per hole won"
                value={config.moneyPerPoint ?? 1}
                onChange={(v) => update(formatId, { moneyPerPoint: v })}
              />
            ) : (
              <StakeRow
                label="Money per point"
                suffix="Stableford pts × $"
                value={config.moneyPerPoint ?? 1}
                onChange={(v) => update(formatId, { moneyPerPoint: v })}
              />
            )}
          </>
        )}
        {!showMoney && (
          <p className="text-[10px] text-mute">Stableford points determine standings — no money on this game.</p>
        )}
      </div>
    );
  }

  if (formatId === 'wolf' || formatId === 'lone-wolf') {
    return (
      <div className="space-y-2">
        <NetHandicapAllowance formatId="wolf" config={config} update={update} forceShow />
        {showMoney && (
          <StakeRow
            label="$ per point"
            suffix="Wolf points × $"
            value={config.moneyPerPoint ?? (typeof config.money === 'number' ? config.money : 1)}
            onChange={(v) => update(formatId, { moneyPerPoint: v, money: v })}
          />
        )}
        {!showMoney && (
          <p className="text-[10px] text-mute">Wolf hole points track standings — configure multipliers in the live round.</p>
        )}
      </div>
    );
  }

  if (formatId === 'best-ball') {
    return (
      <div className="space-y-2">
        <NetHandicapAllowance formatId={formatId} config={config} update={update} forceShow />
        {showMoney ? (
          <div className="grid grid-cols-3 gap-2">
            <MiniStake label="Front 9" value={legs.front} onChange={(v) => updateLeg(formatId, 'front', v)} />
            <MiniStake label="Back 9" value={legs.back} onChange={(v) => updateLeg(formatId, 'back', v)} />
            <MiniStake label="Overall" value={legs.total} onChange={(v) => updateLeg(formatId, 'total', v)} />
          </div>
        ) : (
          <p className="text-[10px] text-mute">Team standings — enable Money or Both for leg stakes.</p>
        )}
      </div>
    );
  }

  if (formatId === 'putts-bet') {
    const mode = config.puttsBetMode ?? 'lowest-total';
    return (
      <div className="space-y-2">
        <OptionRow label="Putts bet mode">
          <Chip
            active={mode === 'lowest-total'}
            onClick={() => update(formatId, { puttsBetMode: 'lowest-total' })}
            label="Total"
          />
          <Chip
            active={mode === 'per-hole'}
            onClick={() => update(formatId, { puttsBetMode: 'per-hole' })}
            label="Per hole"
          />
          <Chip
            active={mode === 'per-putt'}
            onClick={() => update(formatId, { puttsBetMode: 'per-putt' })}
            label="$ per putt"
          />
        </OptionRow>
        <p className="text-[10px] leading-relaxed text-mute">
          {mode === 'lowest-total'
            ? 'Fixed pot — fewest total putts for the round wins. Enter putts on the live scorecard.'
            : mode === 'per-hole'
              ? 'Fewest putts on each hole wins that hole\'s stake. Enter putts on the live scorecard.'
              : 'Each player pays $X for every putt over the round leader. Enter putts on the live scorecard.'}
        </p>
        {showMoney && (
          <StakeRow
            label={mode === 'per-putt' ? '$ per putt over leader' : mode === 'per-hole' ? 'Per hole' : 'Fixed pot'}
            suffix={mode === 'per-putt' ? 'per putt over leader' : mode === 'per-hole' ? 'per hole won' : 'winner takes pot'}
            value={typeof config.money === 'number' ? config.money : 5}
            onChange={(v) => update(formatId, { money: v })}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <NetHandicapAllowance formatId={formatId} config={config} update={update} forceShow />
      {showMoney && supportsStakeBasis(formatId) && formatId !== 'putts-bet' && (
        <StakeBasisRow config={config} formatId={formatId} update={update} totalLabel="Total" />
      )}
      {showMoney && (
        <StakeRow
          label={config.matchStakeMode === 'per-hole' ? 'Per hole' : 'Stake'}
          suffix={config.matchStakeMode === 'per-hole' ? 'per hole won' : 'amount'}
          value={typeof config.money === 'number' ? config.money : 10}
          onChange={(v) => update(formatId, { money: v })}
        />
      )}
      {!showMoney && (
        <p className="text-[10px] text-mute">Standings only — enable Money or Both for stakes.</p>
      )}
    </div>
  );
}

function StakeBasisRow({
  config,
  formatId,
  update,
  totalLabel,
}: {
  config: GameStakeConfig;
  formatId: string;
  update: (id: string, patch: Partial<GameStakeConfig>) => void;
  totalLabel: string;
}) {
  if (!supportsStakeBasis(formatId) || formatId === 'putts-bet') return null;
  return (
    <OptionRow label="Stake basis">
      <Chip
        active={config.matchStakeMode !== 'per-hole'}
        onClick={() => update(formatId, { matchStakeMode: 'per-match' })}
        label={totalLabel}
      />
      <Chip
        active={config.matchStakeMode === 'per-hole'}
        onClick={() => update(formatId, { matchStakeMode: 'per-hole' })}
        label="Per hole"
      />
    </OptionRow>
  );
}

function MatchOpponentPicker({
  formatId,
  roster,
  organizerKey,
  opponentKey,
  onChange,
}: {
  formatId: string;
  roster: RosterEntry[];
  organizerKey: string;
  opponentKey?: string;
  onChange: (formatId: string, opponentKey: string) => void;
}) {
  const opponents = roster.filter((p) => p.player_key !== organizerKey);
  const selected = opponentKey && opponents.some((p) => p.player_key === opponentKey)
    ? opponentKey
    : opponents[0]?.player_key;

  if (opponents.length === 0) return null;

  return (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-mute">Your opponent</div>
      <div className="flex flex-wrap gap-1.5">
        {opponents.map((p) => (
          <Chip
            key={p.player_key}
            active={selected === p.player_key}
            onClick={() => onChange(formatId, p.player_key)}
            label={p.name}
          />
        ))}
      </div>
      {opponents.length >= 2 && roster.length > 2 && (
        <p className="mt-1.5 text-[10px] leading-relaxed text-mute">
          Other players score on the card but are not in this Nassau match.
        </p>
      )}
    </div>
  );
}

function OptionRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-mute">{label}</div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({
  label,
  active,
  onClick,
  disabled,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`rounded-full px-2.5 py-1 text-[11px] font-bold disabled:opacity-60 ${
        active ? 'bg-navy text-cream' : 'border border-line text-ink-soft'
      }`}
    >
      {label}
    </button>
  );
}

function StakeRow({
  label,
  suffix,
  value,
  onChange,
  prefix = '$',
  step = 1,
}: {
  label: string;
  suffix: string;
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
  step?: number;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="text-xs font-bold text-navy">{label}</div>
        <div className="text-[10px] text-mute">{suffix}</div>
      </div>
      <AmountStepper value={value} onChange={onChange} prefix={prefix} step={step} />
    </div>
  );
}

function MiniStake({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="rounded-lg bg-cream-warm p-2 text-center">
      <div className="mb-1 text-[9px] font-bold uppercase text-mute">{label}</div>
      <AmountStepper value={value} onChange={onChange} compact />
    </div>
  );
}

function AmountStepper({
  value,
  onChange,
  compact,
  prefix = '$',
  step = 1,
}: {
  value: number;
  onChange: (v: number) => void;
  compact?: boolean;
  prefix?: string;
  step?: number;
}) {
  const min = step === 1 && prefix === '$' ? 1 : 0;
  return (
    <div className="flex items-center justify-center gap-1.5">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - step))}
        className={`flex items-center justify-center rounded-lg border border-line bg-cream-warm font-extrabold ${compact ? 'h-7 w-7 text-sm' : 'h-8 w-8'}`}
      >
        −
      </button>
      <span className={`min-w-10 text-center font-extrabold ${compact ? 'text-sm' : 'text-base'}`}>
        {prefix}{value}
      </span>
      <button
        type="button"
        onClick={() => onChange(value + step)}
        className={`flex items-center justify-center rounded-lg border border-line bg-cream-warm font-extrabold ${compact ? 'h-7 w-7 text-sm' : 'h-8 w-8'}`}
      >
        +
      </button>
    </div>
  );
}

/** @deprecated use StakesScoringPanel */
export const StakesConfigPanel = StakesScoringPanel;

export function buildInitialStakes(enabledIds: string[]): StakesConfig {
  const stakes: StakesConfig = {};
  enabledIds.forEach((id) => { stakes[id] = defaultStakeForFormat(id); });
  return stakes;
}
